import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { usePortfolio } from '@/context/PortfolioContext';
import { Save, Home, User, Briefcase, Mail, Phone, MapPin, FileText, Linkedin, Github, MessageSquareMore } from 'lucide-react';
import { toast } from 'sonner';

const AdmHero = () => {
  const { portfolioData, updateHero } = usePortfolio();
  const [formData, setFormData] = useState(portfolioData?.hero || {});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setFormData(portfolioData?.hero || {});
  }, [portfolioData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await updateHero(formData);
      if (result?.success) {
        toast.success('Hero modifié avec succès');
      } else {
        toast.error(result?.message || 'Erreur lors de la sauvegarde des informations hero');
      }
    } catch (error) {
      // console.error('Error saving hero information:', error);
      toast.error('Erreur lors de la sauvegarde des informations hero');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setFormData(portfolioData?.hero || {});
    toast.success('Formulaire réinitialisé');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Home className="w-6 h-6 mr-2" />
          Section Hero
        </h2>
        <div className="space-x-2">
          <Button variant="outline" onClick={handleReset}>
            Réinitialiser
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="w-5 h-5 mr-2" />
              Informations Personnelles
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name" className="flex items-center mb-1">Nom Complet</Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Votre nom complet"
              />
            </div>
            <div>
              <Label htmlFor="title" className="flex items-center mb-1">Titre Professionnel</Label>
              <Input
                id="title"
                value={formData.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Votre titre professionnel"
              />
            </div>
            <div>
              <Label htmlFor="tagline" className="flex items-center mb-1">Tagline</Label>
              <Textarea
                id="tagline"
                value={formData.tagline || ''}
                onChange={(e) => handleChange('tagline', e.target.value)}
                placeholder="Votre description professionnelle"
                rows={5}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Mail className="w-5 h-5 mr-2" />
              Coordonnées
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="email" className="flex items-center mb-1">
                <Mail className="w-4 h-4 mr-1" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="votre.email@example.com"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="flex items-center mb-1">
                <Phone className="w-4 h-4 mr-1" />
                Téléphone
              </Label>
              <Input
                id="phone"
                value={formData.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+33 1 23 45 67 89"
              />
            </div>
            <div>
              <Label htmlFor="whatsapp" className="flex items-center mb-1">
                <MessageSquareMore className="w-4 h-4 mr-1" />
                WhatsApp
              </Label>
              <Input
                id="whatsapp"
                value={formData.whatsapp || ''}
                onChange={(e) => handleChange('whatsapp', e.target.value)}
                placeholder="https://wa.me/1234567890"
              />
            </div>
            <div>
              <Label htmlFor="location" className="flex items-center mb-1">
                <MapPin className="w-4 h-4 mr-1" />
                Localisation
              </Label>
              <Input
                id="location"
                value={formData.location || ''}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="Ville, Pays"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Briefcase className="w-5 h-5 mr-2" />
              Réseaux Sociaux & CV
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="github" className="flex items-center mb-1">
                  <Github className="w-4 h-4 mr-1" />
                  GitHub
                </Label>
                <Input
                  id="github"
                  value={formData.github || ''}
                  onChange={(e) => handleChange('github', e.target.value)}
                  placeholder="https://github.com/username"
                />
              </div>
              <div>
                <Label htmlFor="linkedin" className="flex items-center mb-1">
                  <Linkedin className="w-4 h-4 mr-1" />
                  LinkedIn
                </Label>
                <Input
                  id="linkedin"
                  value={formData.linkedin || ''}
                  onChange={(e) => handleChange('linkedin', e.target.value)}
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="cv" className="flex items-center mb-1">
                <FileText className="w-4 h-4 mr-1" />
                Nom du fichier CV
              </Label>
              <Input
                id="cv"
                value={formData.cv || ''}
                onChange={(e) => handleChange('cv', e.target.value)}
                placeholder="nom_du_fichier.pdf"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdmHero;